import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { LifeBuoy, Send } from "lucide-react";
import { endpoints, getTenantScope } from "@/lib/api";
import { toast } from "sonner";

export default function RequestAssistanceModal({ open, onOpenChange, clientSlug, clientName, onCreated }) {
  const [meta, setMeta] = useState({ categories: [], priorities: ["Low", "Normal", "High", "Urgent"] });
  const [me, setMe] = useState(null);
  const [category, setCategory] = useState("");
  const [subject, setSubject] = useState("");
  const [details, setDetails] = useState("");
  const [priority, setPriority] = useState("Normal");
  const [requestedBy, setRequestedBy] = useState("");
  const [requestedByEmail, setRequestedByEmail] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    endpoints.assistanceMetadata().then(setMeta).catch(() => {});
    endpoints.me().then((u) => {
      setMe(u);
      const scope = getTenantScope();
      // Prefill with the mocked user; for client role, prefer their name (still using mock)
      setRequestedBy((prev) => prev || (scope?.role === "client" ? (scope.company_name || u.name) + " user" : u.name));
      setRequestedByEmail((prev) => prev || u.email || "");
    }).catch(() => {});
    setCategory((prev) => prev || "Request Meeting");
  }, [open]);

  const reset = () => {
    setCategory("Request Meeting");
    setSubject("");
    setDetails("");
    setPriority("Normal");
  };

  const submit = async () => {
    if (!clientSlug) { toast.error("No client context — open from a client dashboard"); return; }
    if (!subject.trim() || !details.trim()) { toast.error("Please fill in subject and details"); return; }
    setBusy(true);
    try {
      const created = await endpoints.createAssistanceRequest({
        client_slug: clientSlug, category, subject, details, priority,
        requested_by: requestedBy || "Client user", requested_by_email: requestedByEmail || "",
      });
      toast.success(`Request ${created.request_number} sent to ${created.assigned_to || "ISOQuest"}`);
      onCreated?.(created);
      reset();
      onOpenChange(false);
    } catch (e) {
      const msg = e.response?.data?.detail || "Could not send request";
      toast.error(typeof msg === "string" ? msg : "Could not send request");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="assistance-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="h-9 w-9 rounded-xl brand-gradient text-white flex items-center justify-center shadow-md">
              <LifeBuoy className="h-4 w-4" />
            </span>
            Request ISOQuest Assistance
          </DialogTitle>
          <DialogDescription>
            {clientName ? <>Send a request from <strong>{clientName}</strong> to your ISOQuest consultant.</> : "Choose a category and describe what you need — we'll route it to your consultant."}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs mb-1 block">Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="assistance-category-trigger"><SelectValue placeholder="Select a category" /></SelectTrigger>
                <SelectContent>
                  {(meta.categories || []).map((c) => (
                    <SelectItem key={c} value={c} data-testid={`assistance-cat-${c.replace(/\s+/g, "-").toLowerCase()}`}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs mb-1 block">Priority</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger data-testid="assistance-priority-trigger"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(meta.priorities || []).map((p) => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-xs mb-1 block">Subject</Label>
            <Input
              data-testid="assistance-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Short summary (e.g. Kick-off meeting for Q2 audit)"
              maxLength={140}
            />
          </div>

          <div>
            <Label className="text-xs mb-1 block">Details</Label>
            <Textarea
              data-testid="assistance-details"
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder="Provide as much detail as you can — dates, scope, people involved, links to relevant docs..."
              rows={6}
              maxLength={4000}
            />
            <div className="text-[11px] text-slate-400 mt-1 text-right">{details.length}/4000</div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs mb-1 block">Your name</Label>
              <Input data-testid="assistance-requested-by" value={requestedBy} onChange={(e) => setRequestedBy(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs mb-1 block">Reply-to email <span className="text-slate-400">(optional)</span></Label>
              <Input data-testid="assistance-requested-email" type="email" value={requestedByEmail} onChange={(e) => setRequestedByEmail(e.target.value)} placeholder="you@company.com" />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button
            data-testid="assistance-submit"
            className="brand-gradient text-white"
            onClick={submit}
            disabled={busy || !subject.trim() || !details.trim()}
          >
            <Send className="h-4 w-4 mr-1" /> {busy ? "Sending..." : "Send request"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
